import type { CommandManager } from '@commands';
import { localizedKey, localizedKeyTrigger, StringId } from '@strings';
import { expectDefined, fixedNumber } from '@utils';
import ShoSho from 'shosho';
import { SvelteSet } from 'svelte/reactivity';
import { localeManager } from './LocaleManager.svelte';
import { platformManager } from './PlatformManager.svelte';
import { prefs } from './Preferences.svelte';

// Code values for keyboard events
// https://developer.mozilla.org/en-US/docs/Web/API/UI_Events/Keyboard_event_code_values
//
// Key values for keyboard events
// https://developer.mozilla.org/en-US/docs/Web/API/UI_Events/Keyboard_event_key_values

export class KeyManager {
  commandManager: CommandManager;
  shortcuts: ShoSho | undefined;
  private disposeFunction: (() => void) | undefined;

  lastEvent = $state<KeyboardEvent | undefined>(undefined);
  change: number = $state(0);

  disabled = $state(false);

  modifiersDown = new SvelteSet<string>();
  keysDown = new SvelteSet<string>();

  shift = $derived<boolean>((this as KeyManager).modifiersDown.has(StringId.SHIFT_KEY));
  ctrl = $derived<boolean>((this as KeyManager).modifiersDown.has(StringId.CTRL_KEY));
  alt = $derived<boolean>((this as KeyManager).modifiersDown.has(StringId.ALT_KEY));
  meta = $derived<boolean>((this as KeyManager).modifiersDown.has(StringId.META_KEY));

  disable() {
    this.disabled = true;
  }

  enable() {
    this.disabled = false;
  }

  statusHudContent = $derived.by<string | undefined>(() => {
    if (!prefs.screencastMode) {
      return undefined;
    }
    if (this.modifiersDown.size > 0 /*| this.keysDown.size > 0*/) {
      // const keys = [...Array.from(this.modifiersDown), ...Array.from(this.keysDown)];
      const keys = Array.from(this.modifiersDown);
      const displayKeys = keys.map(key =>
        localizedKey(key as StringId, platformManager.platformId, localeManager.localeId)
      );
      const s = displayKeys.join(' ');
      return `
      <div class="fl-hud-detail">
        <span class="fl-hud-key">
          ${s}
        </span>
      </div>
      `;
    }
    return undefined;
  });
  triggerHudContent = $state<string | undefined>(undefined);

  hudContent = $derived.by<string | undefined>(() => {
    return this.triggerHudContent ?? this.statusHudContent;
  });
  hudContentChange = $state(0);

  hudTimeout = $derived.by<number>(() => {
    // If we're showing trigger content, hide it after a few seconds.
    if (this.triggerHudContent) {
      return 3000;
    } else {
      // If we're showing status content, leave it up indefinitely.
      return 30000;
    }
  });

  setTriggerHudContent(content: string | undefined) {
    if (!prefs.screencastMode) {
      return;
    }
    this.triggerHudContent = content;
    this.hudContentChange++;
  }

  debugString = $derived.by(() => {
    function format(s: string, isCaps: boolean): string {
      return isCaps ? s.toUpperCase() : s.toLowerCase();
    }

    return `${fixedNumber(this.change)} ${format(StringId.SHIFT_KEY, this.shift)} ${format(StringId.CTRL_KEY, this.ctrl)} ${format(StringId.ALT_KEY, this.alt)} ${format(StringId.META_KEY, this.meta)}`;
  });

  constructor(commandManager: CommandManager) {
    this.commandManager = commandManager;
  }

  liftAllKeys() {
    this.keysDown.clear();
    this.modifiersDown.clear();
  }

  handleKeyDown(event: KeyboardEvent) {
    // console.log(`${fixedNumber(this.change)}  DOWN key: ${event.key}, code: ${event.code}`);

    if (this.disabled) {
      return;
    }

    if (event.repeat) {
      return;
    }

    if (isModifierEvent(event)) {
      this.setTriggerHudContent(undefined);
      this.modifiersDown.add(event.key);
    } else {
      this.keysDown.add(event.key);
    }

    this.lastEvent = event;
    this.change++;

    // console.log(`${fixedNumber(this.change)}  DOWN key: ${event.key}, code: ${event.code}`);
  }

  handleKeyUp(event: KeyboardEvent) {
    if (this.disabled) {
      return;
    }

    // console.log(`${fixedNumber(this.change)}  UP key: ${event.key}, code: ${event.code}`);

    if ((event.key as StringId) === StringId.META_KEY) {
      // console.log('META KEY UP');

      this.liftAllKeys();
    } else if (isModifierEvent(event)) {
      // console.log('MODIFIER KEY UP');

      this.modifiersDown.delete(event.key);
    }

    // console.log('DISPATCH KEY UP');
    this.commandManager.dispatchKeyUp();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  handleBlur(event: FocusEvent) {
    // Important if the user switches tabs or windows using the Meta key
    this.liftAllKeys();
    this.lastEvent = undefined;
    this.change++;
    // console.log(`${fixedNumber(this.change)} 🔵  BLUR`);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  handleFocus(event: FocusEvent) {
    this.liftAllKeys();
    this.lastEvent = undefined;
    this.change++;
    // console.log(`${fixedNumber(this.change)} 🔵 FOCUS`);
  }

  install(target: Window | Document | HTMLElement) {
    const keyDown = (event: Event) => {
      this.handleKeyDown(event as KeyboardEvent);
    };
    const keyUp = (event: Event) => {
      this.handleKeyUp(event as KeyboardEvent);
    };
    const blur = (event: Event) => {
      this.handleBlur(event as FocusEvent);
    };
    const focus = (event: Event) => {
      this.handleFocus(event as FocusEvent);
    };
    target.addEventListener('keydown', keyDown);
    target.addEventListener('keyup', keyUp);
    target.addEventListener('blur', blur);
    target.addEventListener('focus', focus);

    this.shortcuts = new ShoSho({
      // capture: true,
      target,
      shouldHandleEvent(event: Event) {
        // Return "true" if this event should be handled
        let shouldHandle = false;
        if (event instanceof KeyboardEvent) {
          shouldHandle = !event.repeat;
        }
        // If event is modifier key down, then it should never trigger a command.
        if (isModifierEvent(event as KeyboardEvent) && event.type === 'keydown') {
          shouldHandle = false;
        }

        return shouldHandle;
      }
    });
    for (const command of this.commandManager.commands.values()) {
      const keyTrigger = command.keyTrigger;
      if (keyTrigger === undefined) {
        continue;
      }
      this.shortcuts.register(keyTrigger, event => {
        // Allow browser to handle events that don't trigger our commands.
        expectDefined(event).preventDefault();

        // Get the name of the command to be triggered *before* dispatching it.
        const name = command.name;

        // Only give feedback if the command was enabled.
        const wasEnabled = this.commandManager.dispatch(command.id);
        if (wasEnabled) {
          this.setTriggerHudContent(`
            <div class="fl-hud-title">
              ${localeManager.localizedString(name)}
            </div>
            <div class="fl-hud-detail">
              <span class="fl-hud-key">
                ${localizedKeyTrigger(keyTrigger, platformManager.platformId, localeManager.localeId)}
              </span>
            </div>
          `);
        }

        this.keysDown.clear();
        return true;
      });
    }

    $effect(() => {
      if (this.disabled) {
        this.shortcuts?.stop();
      } else {
        this.shortcuts?.start();
      }
    });

    this.disposeFunction = () => {
      target.removeEventListener('keydown', keyDown);
      target.removeEventListener('keyup', keyUp);
      target.removeEventListener('blur', blur);
      target.removeEventListener('focus', focus);
      this.shortcuts?.stop();
      this.shortcuts?.reset();
    };
  }

  uninstall() {
    if (this.disposeFunction) {
      this.disposeFunction();
      this.disposeFunction = undefined;
    }
  }
}

function isModifierEvent(event: KeyboardEvent): boolean {
  const key = event.key as StringId;
  return (
    key == StringId.SHIFT_KEY ||
    key == StringId.CTRL_KEY ||
    key == StringId.ALT_KEY ||
    key == StringId.META_KEY
  );
}
